// As aulas de um curso bônus: duração, seções, PROGRESSO e a próxima aula.
//
// PURO, sem IO — a prova é `tests/aulas-bonus.mjs`. Quem lê e grava é
// `lib/cursoBonus.ts`; quem desenha é `paginas/CursoBonus.tsx`. A separação é a
// mesma de `processos.ts` (a regra) e `leitorDeProcessos.ts` (o IO): a regra se
// prova em Node, sem navegador nem banco, e o IO se prova pelo caminho real.
//
// ---------------------------------------------------------------------------
// O que é REGRA aqui, e por que mora no front
// ---------------------------------------------------------------------------
// O banco decide o que é FATO: quem pode ver a lista, quem pode concluir, quando
// cada aula foi concluída e aberta (`bonus_minhas_aulas`, 000183). O que este
// módulo faz é APRESENTAÇÃO derivada desses fatos — quantas de quantas, quanto
// falta, qual é a próxima. É a família de `Curso.tsx` agrupando cards para
// mostrar, não a de "agregar acesso em JavaScript": nenhuma destas funções
// responde "o aluno pode?", e nenhuma grava. Se um dia um número daqui virar
// dado gravado (um certificado por concluir as doze, por exemplo), a conta tem
// de descer para o banco, que é onde a decisão passaria a ser tomada.

export type AulaBonus = {
  /** O id da aula no Memberkit. É ele que viaja para abrir a aula. */
  aulaId: number;
  titulo: string;
  /** A seção do curso, quando o curso tem mais de uma. */
  secao: string | null;
  /** A `position` da FONTE — não é o número que a tela mostra. Ver `porSecao`. */
  ordem: number;
  duracaoSegundos: number | null;
  temMaterial: boolean;
  /** Quando o aluno marcou como concluída (carimbo do servidor). Nulo = não marcou. */
  concluidaEm: string | null;
  /** A ÚLTIMA vez que o aluno clicou em "Assistir" nesta aula. Nulo = nunca. */
  abertaEm: string | null;
  /** O bilhete do aluno sobre a aula. Nulo = não anotou. */
  anotacao: string | null;
};

/**
 * A duração de uma aula, escrita como gente lê.
 *
 * `null` devolve `null` e não "0 min": aula sem vídeo existe (material escrito), e
 * "0 min" ao lado do título faz o aluno achar que o vídeo está quebrado. Ausência
 * de dado não se preenche com zero — a cicatriz do teto do MEI, onde
 * `?? anos[0]?.limite ?? 0` escrevia "de R$ 0,00" num livro vazio.
 */
export function duracaoEmTexto(segundos: number | null): string | null {
  if (segundos === null || !Number.isFinite(segundos) || segundos <= 0) return null;
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `${minutos} min`;
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}

/**
 * O total do curso, para o tile de fato da cabine.
 *
 * Soma só o que TEM duração. Se nenhuma aula tiver, devolve `null` — e o tile não
 * aparece, porque tile de fato só existe com dado medido ("zero grande é ruído",
 * a regra da `Cabine`).
 */
export function duracaoTotalEmTexto(aulas: Pick<AulaBonus, "duracaoSegundos">[]): string | null {
  const total = aulas.reduce((s, a) => s + (a.duracaoSegundos ?? 0), 0);
  return duracaoEmTexto(total > 0 ? total : null);
}

/**
 * As aulas agrupadas por seção, na ordem em que vieram.
 *
 * Um curso pode ter uma seção só (Sheets e IA) ou várias (o Excel tem três). Com
 * uma, a tela não mostra cabeçalho de seção — um único título de grupo repetindo
 * o nome do curso é ruído.
 *
 * A NUMERAÇÃO sai da posição na LISTA, nunca de `ordem`: `ordem` é a `position`
 * do Memberkit, e ela tem buracos onde aulas foram removidas (medido em 04/09 —
 * o Excel vai de 1 a 13 com 12 aulas, e o Sheets tem 1..6 e 8). Mostrar `ordem`
 * escreveria "13." num curso de doze aulas. É a cicatriz do "índice de laço não é
 * número de item" pelo avesso: lá o índice do `.map()` mentia porque rodava
 * depois de um filtro; aqui é o número da FONTE que mente, e quem manda é a
 * posição do que está na tela.
 */
export function porSecao(aulas: AulaBonus[]): { secao: string | null; aulas: AulaBonus[] }[] {
  const grupos: { secao: string | null; aulas: AulaBonus[] }[] = [];
  for (const a of aulas) {
    const ultimo = grupos.at(-1);
    if (ultimo && ultimo.secao === a.secao) ultimo.aulas.push(a);
    else grupos.push({ secao: a.secao, aulas: [a] });
  }
  return grupos;
}

/** O número que a TELA mostra para uma aula: a posição na lista completa, de 1. */
export function numeroDa(aulas: AulaBonus[], aulaId: number): number | null {
  const i = aulas.findIndex((a) => a.aulaId === aulaId);
  return i < 0 ? null : i + 1;
}

export type Progresso = {
  total: number;
  concluidas: number;
  /** 0 a 100, arredondado. Zero com lista vazia — nunca NaN. */
  porcentagem: number;
  /** Todas concluídas — e só com pelo menos uma aula: lista vazia não é curso concluído. */
  concluido: boolean;
  /**
   * A soma das durações das aulas NÃO concluídas. `null` quando nenhuma aula do
   * curso tem duração (aí não há o que dizer); `0` quando tudo está concluído.
   */
  restanteSegundos: number | null;
  /** A MAIOR `concluidaEm` — quando o aluno terminou a última. */
  ultimaConclusaoEm: string | null;
};

/**
 * Quantas de quantas, quanto falta, quando terminou.
 *
 * Tudo aqui é contagem sobre o que o banco devolveu — nenhuma decisão. A
 * porcentagem é arredondada porque "58,333%" numa barra de progresso é precisão
 * que o dado não tem: doze aulas dão passos de 8,3 pontos.
 */
export function progressoDe(aulas: AulaBonus[]): Progresso {
  const total = aulas.length;
  const feitas = aulas.filter((a) => a.concluidaEm !== null);
  const concluidas = feitas.length;
  const temDuracao = aulas.some((a) => (a.duracaoSegundos ?? 0) > 0);
  const restante = aulas
    .filter((a) => a.concluidaEm === null)
    .reduce((s, a) => s + Math.max(0, a.duracaoSegundos ?? 0), 0);
  let ultima: string | null = null;
  for (const a of feitas) {
    if (ultima === null || Date.parse(a.concluidaEm!) > Date.parse(ultima)) ultima = a.concluidaEm;
  }
  return {
    total,
    concluidas,
    porcentagem: total === 0 ? 0 : Math.round((concluidas / total) * 100),
    concluido: total > 0 && concluidas === total,
    restanteSegundos: temDuracao ? restante : null,
    ultimaConclusaoEm: ultima,
  };
}

/**
 * A aula em que o aluno PAROU — o destino de "Continuar".
 *
 * Duas fontes, nesta ordem:
 *
 *  1. a aula NÃO concluída que ele abriu por último (`abertaEm` mais recente
 *     entre as não concluídas). Quem abriu a 5 ontem e não a marcou está na 5,
 *     mesmo com a 2 e a 3 por fazer — é o "continuar assistindo" de qualquer
 *     plataforma de vídeo, e é o que `aberta_em` existe para responder;
 *  2. senão, a primeira não concluída na ORDEM da lista — o aluno que nunca
 *     abriu nada começa pela Aula 1, e o que concluiu 1..4 sem abrir a 5 vai
 *     para a 5.
 *
 * `null` quando não há aula por concluir (curso concluído, ou lista vazia) — a
 * tela troca o CTA nesse caso, e não pode fingir uma próxima.
 *
 * O desempate entre duas aulas abertas no MESMO instante é a posição na lista —
 * estável e determinístico, porque `abertaEm` igual é estado possível (a prova
 * SQL cria as duas na mesma transação, onde `now()` é fixo) e desempate por
 * sorteio é a cicatriz da vw_contrato_a_assinar.
 */
export function proximaAula(aulas: AulaBonus[]): AulaBonus | null {
  const pendentes = aulas.filter((a) => a.concluidaEm === null);
  if (pendentes.length === 0) return null;
  let ultimaAberta: AulaBonus | null = null;
  for (const a of pendentes) {
    if (a.abertaEm === null) continue;
    if (ultimaAberta === null || Date.parse(a.abertaEm) > Date.parse(ultimaAberta.abertaEm!)) {
      ultimaAberta = a;
    }
  }
  return ultimaAberta ?? pendentes[0];
}

/** O aluno já fez ALGO neste curso — concluiu ou abriu alguma aula. */
export function jaComecou(aulas: AulaBonus[]): boolean {
  return aulas.some((a) => a.concluidaEm !== null || a.abertaEm !== null);
}

/**
 * O texto do botão principal da cabine, ou `null` quando não há próxima.
 *
 * "Começar" e "Continuar" são verbos diferentes de propósito: dizer "continuar"
 * a quem nunca abriu nada afirma um histórico que não existe — a mesma família
 * do "cerca de 1 páginas", em que a frase estava certa e o número a desmentia.
 */
export function rotuloDaAcao(aulas: AulaBonus[], proxima: AulaBonus | null): string | null {
  if (!proxima) return null;
  const n = numeroDa(aulas, proxima.aulaId);
  if (n === null) return null;
  return jaComecou(aulas) ? `Continuar na Aula ${n}` : `Começar pela Aula ${n}`;
}

/**
 * "3 de 12 aulas concluídas", "1 de 1 aula concluída" — a concordância é
 * código, não redação (a cicatriz do "cerca de 1 páginas").
 */
export function aulasEmTexto(n: number): string {
  return n === 1 ? "1 aula" : `${n} aulas`;
}
